import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { NgIf, NgForOf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MmDatepickerComponent, AlertBannerComponent } from 'lib';
import { ToastrService } from 'ngx-toastr';
import { SetPreconfigureDateRequest, ScheduleEnablementRequest, OrganizationSiteFilterRequest, PreconfigureMigrationError } from '../../interfaces/preconfigure-migration-dto';
import { OrganizationSiteFilterDto } from '../../interfaces/organization-site-filter-dto';
import { PreconfigureMigrationService } from '../../services/preconfigure-migration.service';
import { DateTime } from 'luxon';
import { HttpErrorResponse } from '@angular/common/http';
import { of, switchMap, tap } from 'rxjs';

@Component({
    selector: 'mmoga-preconfigure-migration-modal',
    standalone: true,
    imports: [
        Dialog,
        NgIf,
        NgForOf,
        FormsModule,
        MmDatepickerComponent,
        AlertBannerComponent
    ],
    templateUrl: './preconfigure-migration-modal.component.html',
    styleUrl: './preconfigure-migration-modal.component.scss'
})
export class PreconfigureMigrationModalComponent implements OnChanges {
    private readonly preconfigureMigrationService = inject(PreconfigureMigrationService);
    private readonly toastrService = inject(ToastrService);

    @Input() showModal = false;
    @Input() selectedSiteIds: number[] = [];
    @Input() isAllSelected = false;
    @Input() totalItems = 0;
    @Input() activeFilters: OrganizationSiteFilterDto | undefined;
    @Input() existingPreconfigureDate: Date | null = null;
    @Input() existingEnablementDate: Date | null = null;
    @Input() excludedCount = 0;
    @Input() canSetPreconfigureDate = true;
    @Input() canSetEnablementDate = true;
    @Output() showModalChange = new EventEmitter<boolean>();
    @Output() bulkActionSaved = new EventEmitter<boolean>();

    preconfigureDate: Date | null = null;
    enablementDate: Date | null = null;
    isSaving = false;
    validationErrors: PreconfigureMigrationError[] = [];

    readonly allPagesSelectedMessage = 'Sites marked as Do Not Enable or already belonging to an organization will be automatically excluded from preconfiguration.';
    readonly minSelectableDate = DateTime.now().plus({ days: 1 }).startOf('day').toJSDate();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['showModal']?.currentValue === true) {
            this.preconfigureDate = this.existingPreconfigureDate ?? null;
            this.enablementDate = this.existingEnablementDate ?? null;
        }
    }

    get excludedCountMessage(): string {
        const sites = this.excludedCount === 1 ? 'site is' : 'sites are';
        return `${this.excludedCount} selected ${sites} not eligible for preconfiguration and will be excluded.`;
    }

    get displayCount(): number {
        return this.isAllSelected ? this.totalItems : this.selectedSiteIds.length;
    }

    get minEnablementDate(): Date {
        if (this.preconfigureDate) {
            const preconfigPlusOne = DateTime.fromJSDate(this.preconfigureDate).plus({ days: 1 }).startOf('day');
            const minLuxon = DateTime.fromJSDate(this.minSelectableDate);
            return preconfigPlusOne > minLuxon ? preconfigPlusOne.toJSDate() : this.minSelectableDate;
        }
        return this.minSelectableDate;
    }

    get enablementDateError(): string | null {
        if (!this.enablementDate || !this.preconfigureDate) return null;
        const enablement = DateTime.fromJSDate(this.enablementDate).startOf('day');
        const preconfig = DateTime.fromJSDate(this.preconfigureDate).startOf('day');
        if (enablement <= preconfig) {
            return 'Enable date must be after the preconfigure date.';
        }
        return null;
    }

    private hasPreconfigChanged(): boolean {
        if (!this.preconfigureDate) return false;
        if (!this.existingPreconfigureDate) return true;
        return !DateTime.fromJSDate(this.preconfigureDate).startOf('day')
            .equals(DateTime.fromJSDate(this.existingPreconfigureDate).startOf('day'));
    }

    private hasEnablementChanged(): boolean {
        if (!this.enablementDate) return false;
        if (!this.existingEnablementDate) return true;
        return !DateTime.fromJSDate(this.enablementDate).startOf('day')
            .equals(DateTime.fromJSDate(this.existingEnablementDate).startOf('day'));
    }

    get canSave(): boolean {
        if (this.isSaving) return false;
        const shouldSavePreconfig = this.canSetPreconfigureDate && this.hasPreconfigChanged();
        const shouldSaveEnablement = this.canSetEnablementDate && this.hasEnablementChanged();
        if (!shouldSavePreconfig && !shouldSaveEnablement) return false;
        if (shouldSavePreconfig && DateTime.fromJSDate(this.preconfigureDate!) < DateTime.fromJSDate(this.minSelectableDate)) return false;
        if (shouldSaveEnablement && DateTime.fromJSDate(this.enablementDate!) < DateTime.fromJSDate(this.minSelectableDate)) return false;
        return !this.enablementDateError;
    }

    getFieldError(field: string): string | null {
        const error = this.validationErrors.find(e => e.field === field);
        return error ? error.message : null;
    }

    save(): void {
        this.validationErrors = [];
        this.isSaving = true;

        const shouldSavePreconfig = this.canSetPreconfigureDate && this.hasPreconfigChanged();
        const shouldSaveEnablement = this.canSetEnablementDate && this.hasEnablementChanged();

        if (!shouldSavePreconfig && !shouldSaveEnablement) {
            this.isSaving = false;
            return;
        }

        let preconfigAlreadySaved = false;

        const preconfig$ = shouldSavePreconfig
            ? this.preconfigureMigrationService.bulkSetPreconfigureDate(this.buildPreconfigureRequest()).pipe(
                tap(() => {
                    preconfigAlreadySaved = true;
                })
            )
            : of(void 0);

        const call$ = shouldSaveEnablement
            ? preconfig$.pipe(switchMap(() => this.preconfigureMigrationService.bulkSetEnablementDate(this.buildEnablementRequest())))
            : preconfig$;

        call$.subscribe({
            next: () => {
                this.isSaving = false;
                this.toastrService.success('Bulk action submitted. Eligible sites will be updated.');
                this.bulkActionSaved.emit(true);
                this.closeModal();
            },
            error: (err) => {
                this.isSaving = false;
                if (preconfigAlreadySaved) {
                    this.toastrService.warning(
                        'Preconfigure date was saved. Enablement date failed to save — please set the enablement date again.'
                    );
                    this.bulkActionSaved.emit(true);
                    this.closeModal();
                } else {
                    this.handleError(err);
                }
            }
        });
    }

    private buildPreconfigureRequest(): SetPreconfigureDateRequest {
        return {
            organizationIds: this.isAllSelected ? [] : this.selectedSiteIds,
            filters: this.isAllSelected ? this.buildFilters() : null,
            scheduledPreconfigDate: DateTime.fromJSDate(this.preconfigureDate!).toFormat('yyyy-MM-dd')
        };
    }

    private buildEnablementRequest(): ScheduleEnablementRequest {
        return {
            organizationIds: this.isAllSelected ? [] : this.selectedSiteIds,
            filters: this.isAllSelected ? this.buildFilters() : null,
            scheduledEnableDate: DateTime.fromJSDate(this.enablementDate!).toFormat('yyyy-MM-dd')
        };
    }

    private buildFilters(): OrganizationSiteFilterRequest {
        const f = this.activeFilters;
        return {
            status: f?.status ?? null,
            belongsToEnterprise: f?.belongsToEnterprise ?? null,
            doNotEnable: f?.doNotEnable ?? null,
            searchTerm: f?.searchTerm ?? null,
            pod: f?.pod ?? null,
            preconfigDateFilter: f?.preconfigDateFilter ?? null,
            enableDateFilter: f?.enableDateFilter ?? null,
            daysInOrganizationFilter: f?.daysInOrganizationFilter ?? null
        };
    }

    private handleError(err: HttpErrorResponse): void {
        if (err.error?.errors && Array.isArray(err.error.errors)) {
            this.validationErrors = err.error.errors;
        } else if (err.error?.message) {
            this.toastrService.error(err.error.message, 'Validation Error');
        } else {
            this.toastrService.error('Failed to save. Please try again.', 'Error');
        }
    }

    closeModal(): void {
        this.resetForm();
        this.showModalChange.emit(false);
    }

    private resetForm(): void {
        this.preconfigureDate = null;
        this.enablementDate = null;
        this.validationErrors = [];
        this.isSaving = false;
    }
}
